import { useEffect, useMemo, useState } from "react"

import { Flex, Pagination, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import SelectComponent from "../../../../../../Components/Select"
import Table, { itemRender } from "../../../../../../Components/Table"
import { useIncognito } from "../../../../../../hooks/useIncognito"
import { getDates } from "../../../../../../Utils/utils"
import { useDemoDataToggle } from "../../../../../User/hooks/useDemoDataToggle"
import { toEpsFilterParams } from "../../../../components/Monitoring/epsFilters"
import EpsNoResults from "../../../../components/Monitoring/EpsNoResults/EpsNoResults"
import { DEFAULT_EPS_ORDERING, toOrdering } from "../../../../components/Monitoring/epsSorting"
import { withMonthRowFlags } from "../../../../components/Monitoring/monthRows"
import HealthActions from "../../../../reducer"
import { useBusinessAccountId } from "../../../hooks/useBusinessAccountId"
import { useVampHistoricalDataMode } from "../../../hooks/useHistoricalDataMode"
import { useVampDataAvailable } from "../../../hooks/useVampDataAvailable"
import { useVampDescriptor } from "../../../hooks/useVampDescriptor"
import { getColumns } from "./tableColumns"

import "./EstimatedProgramStatusTable.less"

const { Text } = Typography

const METRIC_OPTIONS = [
  { label: "VAMP ratio", value: "vamp_rate" },
  { label: "Fraud count", value: "fraud_count" },
  { label: "Dispute count", value: "dispute_count" },
  { label: "Transaction count", value: "transaction_count" },
  { label: "Transaction volume", value: "transaction_volume" }
]

const EstimatedProgramStatusTable = ({
  variant,
  pageSize,
  withPagination,
  search = "",
  columnFilters
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { userInfo, isAdmin } = useSelector((state) => state.auth)
  const { isShowDemoData } = useDemoDataToggle()
  const [pageNumber, setPageNumber] = useState(1)
  const {
    vampEstimatedProgramStatus,
    vampEstimatedProgramStatusFetching,
    filters: pageFilters,
    vampMainChart
  } = useSelector((state) => state.health)
  const descriptor = useVampDescriptor()
  const isIncognito = useIncognito()
  const vampLimit = Number(vampMainChart?.threshold?.rate) || 0
  const isPreview = variant === "block"
  const [ordering, setOrdering] = useState(DEFAULT_EPS_ORDERING)

  const filterParams = useMemo(
    () => (isPreview ? {} : toEpsFilterParams(columnFilters)),
    [isPreview, columnFilters]
  )
  const queryKey = JSON.stringify([search, ordering, pageFilters?.period, filterParams])
  const [lastQueryKey, setLastQueryKey] = useState(queryKey)
  if (queryKey !== lastQueryKey) {
    setLastQueryKey(queryKey)
    setPageNumber(1)
  }
  const { isMobile } = useSelector((state) => state.nav)
  const [selectedMetric, setSelectedMetric] = useState("vamp_rate")

  const isVampDataAvailable = useVampDataAvailable()
  const business_account_id = useBusinessAccountId()
  const { isHistoricalDataMode } = useVampHistoricalDataMode()
  const { results, count } = vampEstimatedProgramStatus ?? {}

  const data = useMemo(() => {
    return results?.map((item) => ({
      ...item,
      months: Object.values(item?.data || {}),
      data: null
    }))
  }, [results])

  const [expandedKeys, setExpandedKeys] = useState([])

  useEffect(() => {
    setExpandedKeys([])
  }, [results])

  useEffect(() => {
    if (descriptor && isVampDataAvailable) {
      dispatch(
        HealthActions.getVampEstimatedProgramStatusRequest({
          business_account: business_account_id,
          page_size: pageSize,
          page: withPagination ? pageNumber : 1,
          descriptor,
          ...getDates(pageFilters?.period),
          ...(isPreview ? { ordering: DEFAULT_EPS_ORDERING } : {}),
          ...(!isPreview && ordering ? { ordering } : {}),
          ...(!isPreview && search.trim() ? { search: search.trim() } : {}),
          ...filterParams,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [
    business_account_id,
    pageFilters?.period,
    isPreview,
    ordering,
    search,
    filterParams,
    pageNumber,
    pageSize,
    withPagination,
    isShowDemoData,
    descriptor,
    isHistoricalDataMode,
    isVampDataAvailable
  ])

  const dataSource = useMemo(() => {
    if (!results) return []

    const mainRows = data?.reduce((acc, item) => {
      const descriptor_row = {
        ...item,
        unique_id: item?.billing_descriptor
      }
      if (expandedKeys.includes(descriptor_row.billing_descriptor)) {
        const months = withMonthRowFlags(descriptor_row?.months).map((month) => ({
          ...month,
          row_type: "month",
          unique_id: `${descriptor_row?.unique_id}-${month?.month_name}`
        }))
        return [...acc, descriptor_row, ...months]
      }
      return [...acc, descriptor_row]
    }, [])

    return [...mainRows]
  }, [results, expandedKeys])

  const tableSettings = useMemo(() => ({ scroll: { x: "auto" } }), [])

  const columns = useMemo(
    () =>
      getColumns({
        t,
        expandedKeys,
        isMobile,
        selectedMetric,
        vampLimit,
        isIncognito,
        sortable: !isPreview,
        search
      }),
    [t, expandedKeys, isMobile, selectedMetric, vampLimit, isIncognito, isPreview, search]
  )

  const rowClickHandler = (record) => {
    if (record.row_type === "month") return
    if (!record?.months?.length) return
    const key = record.unique_id
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(key) ? prevKeys.filter((k) => k !== key) : [...prevKeys, key]
    )
  }

  return (
    <div>
      {isMobile && (
        <Flex
          gap={8}
          style={{ marginBottom: 12 }}
          className="estimated-program-status-mobile-selector"
        >
          <SelectComponent
            size="large"
            value={selectedMetric}
            onChange={setSelectedMetric}
            options={METRIC_OPTIONS}
            style={{ width: "100%" }}
            className="estimated-program-status-metric-select"
          ></SelectComponent>
        </Flex>
      )}

      <Table
        columns={columns}
        rowKey={"unique_id"}
        scroll={tableSettings.scroll}
        showSizeChanger={false}
        withOutPagination={true}
        dataSource={dataSource}
        loading={vampEstimatedProgramStatusFetching}
        onChange={(_pagination, _filters, sorter) => setOrdering(toOrdering(sorter))}
        EmptyComponent={
          search.trim() || Object.keys(filterParams).length ? EpsNoResults : undefined
        }
        onRow={(record) => {
          const isExpandable = record?.months?.length > 0
          return {
            onClick: () => rowClickHandler(record),
            className: cls({
              "expandable-row": isExpandable,
              expanded: expandedKeys.includes(record?.unique_id),
              "month-row": record?.row_type === "month",
              "month-row--last": record?.is_last_month,
              "month-row--future": record?.month_state === "future"
            })
          }
        }}
        className={cls("estimated-program-status-table", variant, { "is-mobile": isMobile })}
      />

      {withPagination && (
        <Pagination
          rootClassName={"ant-table-pagination by-reason-code-pagination"}
          onChange={(page) => setPageNumber(page)}
          total={count || 0}
          pageSize={pageSize}
          current={pageNumber}
          showLessItems={true}
          showTotal={(total, range) => (
            <Text>
              Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
            </Text>
          )}
          itemRender={(_, type, originalElement) =>
            pageSize >= count ? null : itemRender(_, type, originalElement, t)
          }
          showSizeChanger={false}
          showQuickJumper={false}
        />
      )}
    </div>
  )
}

export default EstimatedProgramStatusTable
